import { useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "./useApiClient";
import { getTodayTimeRange } from "../utils/dateUtils";
import { apiRoutes } from "../api/routes";
import { getAlertsApi, ENABLED_ALERT_TYPES_QUERY_KEY } from "@/features/operations/api/alertsApi";

/**
 * ALERT TYPE SCOPE — one rule, several copies: KEEP THEM IN SYNC.
 * Counted alert types = the agency's ENABLED types from K3 `alert_type`:
 *   enabled = true OR type = 13 (Vehicle Emergency), excluding message types 11 / 12;
 *   no enabled type → no type filter.
 * Source of truth: legacy drt_ui AlertType.enabled_alert_type_ids (app/models/alert_type.rb),
 * used by its Alerts page (AlertQuery#scope_default) and header badge
 * (AlertQuery#statistic_expr_status_alert_count).
 * Copies — change one, change all:
 *   - dispatcher_ui  src/features/operations/api/alertsApi.ts         getEnabledAlertTypes / getAlertTypes (Alerts page)
 *   - dispatcher_ui  src/shared/hooks/useNotificationCount.ts         header alert badge
 *   - transitos      ui/apps/dispatcher-mfe/src/features/dashboard/api/alertsSummaryLive.ts
 *                    fetchEnabledAlertTypeIds (dashboard Alert summary widget, Live)
 *   - dispatcher_bff src/ods/ods.service.ts                            enabledAlertTypeSql (Alert summary, Historical — Databricks k3_alert_types)
 *   - dispatcher_bff src/dashboard/dashboard-widgets.definitions.ts    ENABLED_ALERT_TYPES_BODY + dashboard-widgets.service.ts enabledAlertTypeIds
 */
/** Cache entry (ENABLED_ALERT_TYPES_QUERY_KEY) shared with AlertDetailsPage — alert types rarely change. */
const ENABLED_ALERT_TYPES_STALE_MS = 10 * 60 * 1000;

export const useNotificationCount = (enabled = true) => {
  const [notificationCount, setNotificationCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  useEffect(() => {
    // Don't poll when the caller lacks alert_view (the indicator is hidden).
    if (!enabled) {
      setNotificationCount(0);
      setIsLoading(false);
      return;
    }

    let isMounted = true;
    let interval: ReturnType<typeof setInterval> | undefined;

    const fetchNotificationCount = async () => {
      setIsLoading(true);
      try {
        const { start, end } = getTodayTimeRange();
        // The Alerts page's scope: the agency's enabled alert types (see ALERT TYPE SCOPE above).
        const { enabledTypeIds } = await queryClient.fetchQuery({
          queryKey: ENABLED_ALERT_TYPES_QUERY_KEY,
          queryFn: () => getAlertsApi(apiClient).getEnabledAlertTypes(),
          staleTime: ENABLED_ALERT_TYPES_STALE_MS,
        });
        const requestBody = {
          select: ["id"],
          query: [
            ["between", "timestamp", start, end],
            ["null", "resolve_timestamp"],
            ...(enabledTypeIds.length > 0 ? [["in", "type", enabledTypeIds]] : []),
          ],
          options: {
            timeout: 10,
            limit: 0,
            calculate_total: true,
            return_results: false,
            select_distinct: true,
          },
        };

        const response = await apiClient.post(apiRoutes.proxy("support/internal/alert/select_query"), requestBody);

        if (!isMounted) return;
        if (response.data && response.data.info) {
          setNotificationCount(response.data.info?.total_results);
        } else {
          setError("Failed to fetch notification count");
        }
      } catch (err: unknown) {
        if (!isMounted) return;
        // Stop polling permanently when unauthorized — mirrors legacy
        // alert_status.es6:135-137 (freq = -1 on 401).
        const status = (err as { response?: { status?: number } })?.response?.status;
        if (status === 401 || status === 403) {
          if (interval) clearInterval(interval);
          return;
        }
        if (err instanceof Error) {
          setError(err.message);
        } else {
          setError("An unknown error occurred");
        }
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    fetchNotificationCount();
    interval = setInterval(fetchNotificationCount, 60000);

    return () => {
      isMounted = false;
      if (interval) clearInterval(interval);
    };
  }, [apiClient, enabled, queryClient]);

  return { notificationCount, isLoading, error };
};
